import React, { useState, useCallback } from 'react';
import { theme } from '../theme';
import { Input, Button, Badge } from './ui';
import Icons from './Icons';
import { apiPost } from '../api';
import { useConfig } from '../context/ConfigContext';
import { backendOf, isWmsConnection, isComplete, FUSION, WMS } from '../utils/connectionRouting';

// ---------------------------------------------------------------------------
// Connections Manager — list view + inline add/edit form
// ---------------------------------------------------------------------------

// ── Small helper styles ───────────────────────────────────────────────────
const listItemBase = {
  borderRadius: '6px',
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  cursor: 'pointer',
  transition: 'background 0.1s',
  background: 'transparent',
};

// ── Status dot (validity indicator) ──────────────────────────────────────
function StatusDot({ status }) {
  const bg =
    status === 'ok'      ? theme.success :
    status === 'fail'    ? theme.danger :
    status === 'testing' ? theme.warning :
    theme.border;
  const shadow =
    status === 'ok'   ? `0 0 6px ${theme.success}99` :
    status === 'fail' ? `0 0 6px ${theme.danger}99`  : 'none';
  return (
    <div style={{
      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
      background: bg, boxShadow: shadow,
      transition: 'background 0.3s, box-shadow 0.3s',
    }} />
  );
}

// ── Connection list item ──────────────────────────────────────────────────
function ConnectionItem({ conn, isDefault, status, onActivate, onEdit, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const rowBg = confirming
    ? `${theme.danger}0d`
    : isDefault
      ? `${theme.accent}18`
      : hovered ? theme.surfaceHover : 'transparent';

  return (
    <div
      onClick={() => { if (!confirming) onActivate(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...listItemBase,
        cursor: confirming ? 'default' : 'pointer',
        background: rowBg,
        borderColor: confirming
          ? `${theme.danger}55`
          : isDefault ? `${theme.accent}55` : theme.border,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Validity dot */}
        <StatusDot status={status} />

        <span style={{
          flex: 1, fontSize: '13px', fontWeight: isDefault ? 600 : 400,
          color: confirming ? theme.danger : theme.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {confirming ? `Delete "${conn.name || 'Unnamed'}"?` : (conn.name || 'Unnamed')}
        </span>

        {/* Action buttons */}
        {confirming ? (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', gap: '4px', flexShrink: 0 }}
          >
            <button
              onClick={() => { onDelete(); setConfirming(false); }}
              title="Confirm delete"
              style={{
                background: theme.danger, border: 'none', color: '#fff',
                cursor: 'pointer', padding: '2px 7px', borderRadius: '3px',
                fontSize: '11px', fontWeight: 600,
              }}
            >
              Delete
            </button>
            <button
              onClick={() => setConfirming(false)}
              title="Cancel"
              style={{
                background: 'none', border: `1px solid ${theme.border}`,
                color: theme.textMuted, cursor: 'pointer',
                padding: '2px 6px', borderRadius: '3px', fontSize: '11px',
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex', gap: '4px', opacity: hovered ? 1 : 0,
              transition: 'opacity 0.15s',
            }}
          >
            <button
              onClick={onEdit}
              title="Edit"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: theme.textMuted, padding: '2px 4px', borderRadius: '4px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.text; e.currentTarget.style.background = theme.surfaceHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'none'; }}
            >
              <Icons.Edit style={{ width: 12, height: 12 }} />
            </button>
            <button
              onClick={() => setConfirming(true)}
              title="Delete"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: theme.textMuted, padding: '2px 4px', borderRadius: '4px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.danger; e.currentTarget.style.background = `${theme.danger}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; e.currentTarget.style.background = 'none'; }}
            >
              <Icons.X style={{ width: 12, height: 12 }} />
            </button>
          </div>
        )}
      </div>

      {/* Default badge — own line so it never crowds the name */}
      {isDefault && !confirming && (
        <div style={{ marginLeft: 16, marginTop: '3px' }}>
          <span style={{
            fontSize: '10px', fontWeight: 600,
            color: theme.accent,
            background: `${theme.accent}22`,
            border: `1px solid ${theme.accent}44`,
            borderRadius: '4px',
            padding: '1px 5px',
          }}>
            DEFAULT
          </span>
        </div>
      )}

    </div>
  );
}

// ── Add / Edit form ───────────────────────────────────────────────────────
function ConnectionForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    name:     initial?.name     || '',
    // Connections saved before WMS support existed have no type — they are Fusion.
    type:     backendOf(initial),
    baseUrl:  initial?.baseUrl  || '',
    env:      initial?.env      || '',
    username: initial?.username || '',
    password: initial?.password || '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const set = (field) => (v) => {
    setForm(f => ({ ...f, [field]: v }));
    setTestResult(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiPost('/bip/test-connection', {
        baseUrl: form.baseUrl, username: form.username, password: form.password,
        connectionType: backendOf(form), env: form.env || '',
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    }
    setTesting(false);
  };

  const isWms   = isWmsConnection(form);
  const canSave = isComplete(form);


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Back link */}
      <button
        onClick={onCancel}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.textMuted, fontSize: '12px',
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: 0, marginBottom: '2px',
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = theme.text}
        onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
      >
        ← {initial ? `Edit: ${initial.name}` : 'New Connection'}
      </button>

      <Input label="Connection Name" value={form.name} onChange={set('name')} placeholder="e.g. Dev Pod" />

      {/* Backend selector — decides which executor the server dispatches to. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <label style={{ fontSize: '12px', color: theme.textMuted, fontWeight: 500 }}>System</label>
        <select
          value={form.type}
          onChange={(e) => set('type')(e.target.value)}
          style={{
            padding: '8px 12px', background: theme.codeBg,
            border: `1px solid ${theme.border}`, borderRadius: '6px',
            color: theme.text, fontSize: '13px', outline: 'none',
          }}
        >
          <option value={FUSION}>Oracle Fusion (BI Publisher)</option>
          <option value={WMS}>Oracle WMS Cloud (lgfapi)</option>
        </select>
      </div>

      <Input
        label={isWms ? 'WMS URL' : 'Oracle Fusion URL'}
        value={form.baseUrl}
        onChange={set('baseUrl')}
        placeholder={isWms
          ? 'https://xx.wms.ocs.oraclecloud.com'
          : 'https://instance.fa.ocs.oraclecloud.com'}
      />

      {isWms && (
        <Input
          label="Environment"
          value={form.env}
          onChange={set('env')}
          placeholder="e.g. sarcopr — the path segment after the host"
        />
      )}

      <Input label="Username" value={form.username} onChange={set('username')} placeholder="user@company.com" />
      <Input label="Password" value={form.password} onChange={set('password')}
        placeholder="Password" type="password" autoComplete="new-password" selectOnFocus />

      {/* Test Connection */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <Button size="sm" variant="outline" onClick={handleTest} disabled={testing || !form.baseUrl || !form.username}
          icon={<Icons.Zap style={{ width: 12, height: 12 }} />}>
          {testing ? 'Testing…' : 'Test Connection'}
        </Button>
        {testResult && (
          <span style={{
            fontSize: '12px',
            color: testResult.success ? theme.success : theme.danger,
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            {testResult.success ? '✓ Credentials valid — click Save to apply' : `✗ ${testResult.error || 'Failed'}`}
          </span>
        )}
      </div>

      {/* Save / Cancel */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" disabled={!canSave || saving} onClick={() => onSave(form)}>
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Connection'}
        </Button>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export default function ConnectionPanel() {
  const {
    connections, activeConnectionId,
    addConnection, updateConnection, deleteConnection, activateConnection,
    cloudMode, cloudLoaded,
    connectionStatuses, testConnection,
  } = useConfig();

  const statuses = connectionStatuses;

  // view: 'list' | 'form'
  // editingId: null = new, string = editing existing
  const [view, setView] = useState('list');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const openNew  = () => { setEditingId(null); setView('form'); };
  const openEdit = (id) => { setEditingId(id); setView('form'); };
  const cancel   = () => setView('list');

  const handleSave = useCallback(async (form) => {
    if (editingId) {
      updateConnection(editingId, form);
      // Re-test immediately so the dot reflects the new credentials
      testConnection({ id: editingId, ...form });
      setView('list');
    } else {
      setSaving(true);
      let newId;
      try {
        newId = await addConnection(form);
      } finally {
        setSaving(false);
      }
      if (newId) testConnection({ id: newId, ...form });
      setView('list');
    }
  }, [editingId, addConnection, updateConnection, testConnection]);

  const handleDelete = useCallback((id) => {
    deleteConnection(id);
  }, [deleteConnection]);

  // ── List view ──────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
          <span style={{ fontSize: '12px', color: theme.textMuted }}>
            {connections.length} connection{connections.length !== 1 ? 's' : ''}
          </span>
          <Button size="sm" variant="outline" onClick={openNew}
            icon={<Icons.Plus style={{ width: 11, height: 11 }} />}>
            New
          </Button>
        </div>

        {connections.length === 0 && cloudMode && !cloudLoaded && (
          <div style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', padding: '20px 0' }}>
            Loading…
          </div>
        )}
        {connections.length === 0 && (!cloudMode || cloudLoaded) && (
          <div style={{
            fontSize: '12px', color: theme.textMuted, textAlign: 'center',
            padding: '20px 0', lineHeight: 1.5,
          }}>
            No connections yet.<br />
            Click <strong>New</strong> to add your first connection.
          </div>
        )}

        {connections.map(conn => (
          <ConnectionItem
            key={conn.id}
            conn={conn}
            isDefault={conn.id === activeConnectionId}
            status={statuses[conn.id] || null}
            onActivate={() => activateConnection(conn.id)}
            onEdit={() => openEdit(conn.id)}
            onDelete={() => handleDelete(conn.id)}
          />
        ))}

        <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '4px' }}>
          {cloudMode
            ? 'Click a connection to set as default. Credentials are encrypted and synced to your account.'
            : 'Click a connection to set as default. Credentials are stored in this browser only.'}
        </div>
      </div>
    );
  }

  // ── Form view ──────────────────────────────────────────────────────────
  const editing = editingId ? connections.find(c => c.id === editingId) : null;
  return (
    <ConnectionForm
      saving={saving}
      initial={editing}
      onSave={handleSave}
      onCancel={cancel}
    />
  );
}
